import React from 'react';
import styles from './enrichment-asn-information.module.sass';
import { Typography, Tooltip, Table } from 'antd';
import { ColumnTitle } from '../../../../shared-components/ColumnTitle';
import ProgressBar from '../../../../shared-components/ProgressBar';
import LoadingSkeleton from '../../../../shared-components/LoadingSkeleton';
import { RightOutlined, InfoCircleFilled } from '@ant-design/icons';
import { formatDate, formatDaysTag } from '../../../../redux/utils/date';

function EnrichmentAsnInformation({ newData, statusNew }) {
  const { Title, Text } = Typography;
  let asnInformation = undefined;

  if (newData?.response?.ip2asn) {
    asnInformation = newData?.response?.ip2asn?.[0];
  }

  const columns = [
    {
      title: <ColumnTitle columnName="Name" />,
      dataIndex: 'name',
      key: 'name',
      className: styles.name_column,
      render: (value, params) => {
        if (params?.tooltip) {
          return (
            <>
              {value + ' '}
              <Tooltip placement="top" title={params?.tooltip}>
                <InfoCircleFilled />
              </Tooltip>
            </>
          );
        } else {
          return value;
        }
      },
    },
    {
      title: <ColumnTitle columnName="Value" />,
      dataIndex: 'value',
      key: 'value',
    },
    {
      dataIndex: 'progress',
      key: 'progress',
      width: 100,
    },
    {
      dataIndex: 'expandIcon',
      key: 'expandIcon',
      align: 'right',
    },
  ];

  function returnTableData({ loading }) {
    return [
      {
        key: 1,
        name: 'ASN',
        tooltip: 'The number of the AS, allocated by IANA',
        value: loading ? (
          <LoadingSkeleton />
        ) : (
          <Text strong>{asnInformation?.asn ? asnInformation?.asn : '0'}</Text>
        ),
        progress: true,
      },
      {
        key: 2,
        name: 'AS Name',
        tooltip: 'The name of the AS',
        value: loading ? (
          <LoadingSkeleton />
        ) : (
          <Text strong>
            {asnInformation?.asname ? asnInformation?.asname : '-'}
          </Text>
        ),
        progress: null,
      },
      {
        key: 3,
        name: 'ASN Rank',
        tooltip:
          'A ranking of ASNs seen to host threats listed on feeds, calculated using a weighted formula based on the type of threat observed',
        value: loading ? (
          <LoadingSkeleton />
        ) : (
          <Text strong>
            {asnInformation?.asn_rank ? asnInformation?.asn_rank : '0'}
          </Text>
        ),
        progress: loading ? (
          <LoadingSkeleton />
        ) : (
          <ProgressBar
            bgcolor={asnInformation?.asn_rank_score}
            width={'72'}
            completed={asnInformation?.asn_rank_score}
          />
        ),
      },
      {
        key: 4,
        name: 'ASN Takedown Reputation',
        tooltip:
          'A reputation score based on the time it takes for the ASN owner to react to takedown requests related to malicious URLs - a higher reputation score indicates the ASN owner is slow to react to takedown requests',
        value: loading ? (
          <LoadingSkeleton />
        ) : (
          <>
            <Text strong>
              {asnInformation?.asn_takedown_reputation ||
              asnInformation?.asn_takedown_reputation == 0
                ? asnInformation?.asn_takedown_reputation
                : '-'}
            </Text>{' '}
            <Text>
              {asnInformation?.asn_takedown_reputation ||
              asnInformation?.asn_takedown_reputation == 0
                ? 'score'
                : null}
            </Text>
          </>
        ),
        children: [
          {
            key: 41,
            name: 'IPs in ASN',
            tooltip:
              'The number of IP addresses in this ASN that have been seen as listed on trusted feeds',
            value: (
              <Text strong>
                {asnInformation?.asn_takedown_reputation_explain?.ips_in_asn
                  ? asnInformation?.asn_takedown_reputation_explain?.ips_in_asn
                  : '-'}
              </Text>
            ),
            expandable: false,
            progress: null,
          },
          {
            key: 42,
            name: 'IPs with URLs listed',
            tooltip:
              'The number of IP addresses, with URL hosted, in this ASN that have been seen as listed on trusted feeds',
            value: (
              <Text strong>
                {asnInformation?.asn_takedown_reputation_explain?.ips_num_listed
                  ? asnInformation?.asn_takedown_reputation_explain
                      ?.ips_num_listed
                  : '-'}
              </Text>
            ),
            expandable: false,
            progress: null,
          },
          {
            key: 43,
            name: 'Number of URLs listed',
            tooltip:
              'The number of URLs pointing to IP addresses in this ASN that have been seen as listed on trusted feeds',
            value: (
              <Text strong>
                {asnInformation?.asn_takedown_reputation_explain
                  ?.items_num_listed
                  ? asnInformation?.asn_takedown_reputation_explain
                      ?.items_num_listed
                  : '-'}
              </Text>
            ),
            expandable: false,
            progress: null,
          },
          {
            key: 44,
            name: 'Listing Max Age',
            tooltip:
              'The max age of any URL pointing to IP addresses in this ASN that have been seen as listed on trusted feeds',
            value: (
              <Text strong>
                {asnInformation?.asn_takedown_reputation_explain?.lifetime_max
                  ? asnInformation?.asn_takedown_reputation_explain
                      ?.lifetime_max
                  : '-'}
              </Text>
            ),
            expandable: false,
            progress: null,
          },
          {
            key: 45,
            name: 'Listing Average',
            tooltip: '',
            value: (
              <Text strong>
                {asnInformation?.asn_takedown_reputation_explain?.lifetime_avg
                  ? asnInformation?.asn_takedown_reputation_explain
                      ?.lifetime_avg
                  : '-'}
              </Text>
            ),
            expandable: false,
            progress: null,
          },
          {
            key: 46,
            name: 'Total Listing',
            tooltip: '',
            value: (
              <Text strong>
                {asnInformation?.asn_takedown_reputation_explain?.lifetime_total
                  ? asnInformation?.asn_takedown_reputation_explain
                      ?.lifetime_total
                  : '-'}
              </Text>
            ),
            expandable: false,
            progress: null,
          },
        ],
        progress: loading ? (
          <LoadingSkeleton />
        ) : (
          <ProgressBar
            bgcolor={asnInformation?.asn_takedown_reputation_score}
            width={'72'}
            completed={asnInformation?.asn_takedown_reputation_score}
          />
        ),
        expandIcon: (
          <Text strong>
            <RightOutlined id="4" className={styles.expand_indicator} />
          </Text>
        ),
      },
      {
        key: 5,
        name: 'ASN Allocation Age',
        tooltip: 'The age of an AS number in days',
        value: loading ? (
          <LoadingSkeleton />
        ) : (
          <>
            <Text strong>
              {asnInformation?.asn_allocation_age
                ? asnInformation?.asn_allocation_age
                : '-'}
            </Text>{' '}
            <Text>
              {formatDaysTag(asnInformation?.asn_allocation_age, true)}
            </Text>
          </>
        ),
        progress: null,
      },
      {
        key: 6,
        name: 'ASN Allocation Date',
        tooltip: 'The IANA-allocated number of the AS',
        value: loading ? (
          <LoadingSkeleton />
        ) : (
          <Text strong>
            {asnInformation?.asn_allocation_date
              ? formatDate(asnInformation?.asn_allocation_date)
              : '-'}
          </Text>
        ),
        progress: null,
      },
      {
        key: 77,
        name: 'ASN Reputation',
        tooltip:
          'The ratio of blacklisted IPs, taken from from the total number of IPs that have been observed as being active within an ASN, in the last 30 days',
        value: loading ? (
          <LoadingSkeleton />
        ) : (
          <Text strong>
            {asnInformation?.asn_reputation
              ? asnInformation?.asn_reputation
              : '0'}
          </Text>
        ),
        children: [
          {
            key: 771,
            name: 'IPs in ASN',
            tooltip: 'The number of IP addresses available in this ASN',
            value: (
              <Text strong>
                {asnInformation?.asn_reputation_explain?.ips_in_asn
                  ? asnInformation?.asn_reputation_explain?.ips_in_asn
                  : '-'}
              </Text>
            ),
            expandable: false,
            progress: null,
          },
          {
            key: 772,
            name: 'Active IPs',
            tooltip:
              'The number of IP addresses seen as active A records in this ASN',
            value: (
              <Text strong>
                {asnInformation?.asn_reputation_explain?.ips_num_active
                  ? asnInformation?.asn_reputation_explain?.ips_num_active
                  : '-'}
              </Text>
            ),
            expandable: false,
            progress: null,
          },
          {
            key: 773,
            name: 'IPs listed',
            tooltip:
              'The number of IP addresses in this ASN that have been seen as listed on trusted feeds',
            value: (
              <Text strong>
                {asnInformation?.asn_reputation_explain?.ips_num_listed
                  ? asnInformation?.asn_reputation_explain?.ips_num_listed
                  : '-'}
              </Text>
            ),
            expandable: false,
            progress: null,
          },
        ],
        progress: loading ? (
          <LoadingSkeleton />
        ) : (
          <ProgressBar
            bgcolor={asnInformation?.asn_reputation_score}
            width={'72'}
            completed={asnInformation?.asn_reputation_score}
          />
        ),
        expandIcon: (
          <Text strong>
            <RightOutlined id="77" className={styles.expand_indicator} />
          </Text>
        ),
      },
    ];
  }

  return (
    <div
      id="enrichmentAsnInformation"
      className={styles.enrichment_asn_information}
    >
      <Title level={4}>ASN Information</Title>

      <Table
        columns={columns}
        pagination={false}
        expandable={{
          rowExpandable: (record) => record?.children !== undefined,
          expandIcon: ({ expanded, onExpand, record }) => {
            if (statusNew === 'loaded') {
              if (expanded && record?.expandIcon) {
                if (
                  document.getElementById(record?.key) &&
                  record?.expandIcon
                ) {
                  document
                    .getElementById(record?.key)
                    .classList.add('expanded');
                }
              } else {
                if (
                  document.getElementById(record?.key) &&
                  record?.expandIcon
                ) {
                  document
                    .getElementById(record?.key)
                    .classList.remove('expanded');
                }
              }
            }
            return <></>;
          },
          expandRowByClick: true,
          defaultExpandAllRows: true,
        }}
        dataSource={returnTableData({ loading: statusNew === 'loading' })}
      />
    </div>
  );
}

export default EnrichmentAsnInformation;
